import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle } from "lucide-react";
import { INSURANCE_PRODUCTS_LIST } from "../lib/insuranceProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

const SERVICE_OPTIONS = [
  ...INSURANCE_PRODUCTS_LIST.map((p) => p.title),
  "Insurance Needs Assessment",
  "Not sure — help me choose",
];

const initialForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  preferredContact: "",
  state: "",
  dateOfBirth: "",
  veteranStatus: "",
  serviceInterest: "",
  bestTimeToContact: "",
  additionalComments: "",
  consent: false,
};

const inputCls =
  "w-full bg-slate-50 border border-slate-200 px-4 py-3 focus:border-slate-900 focus:ring-0 rounded-sm outline-none transition-colors";

export const InsuranceInquiryModal = ({ isOpen, onClose, defaultService = "" }) => {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (isOpen && defaultService) {
      setFormData((prev) => ({ ...prev, serviceInterest: defaultService }));
    }
  }, [isOpen, defaultService]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      alert("Please provide consent to be contacted.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/insurance-inquiry`, formData);
      setStatus("success");
    } catch (err) {
      console.error("Insurance inquiry submission error:", err);
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFormData(initialForm);
      setStatus(null);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[60] flex items-start md:items-center justify-center p-4 overflow-y-auto"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto my-4 rounded-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-[#0B2343] p-6 sticky top-0 z-10">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-serif text-2xl font-bold text-white">Insurance Inquiry</h2>
                <p className="text-slate-400 text-sm mt-1">
                  We'll follow up within 24 hours
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white"
                data-testid="insurance-close"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {status === "success" ? (
            <div className="p-8 text-center">
              <CheckCircle className="text-green-600 mx-auto mb-4" size={64} />
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
              <p className="text-slate-600 mb-6">
                Your inquiry has been received. Miguel will reach out to you soon.
              </p>
              <button
                onClick={handleClose}
                className="bg-slate-900 text-white px-8 py-3 font-medium hover:bg-slate-800 transition-colors"
                data-testid="insurance-close-success"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="First Name" required>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-firstname"
                  />
                </Field>
                <Field label="Last Name" required>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-lastname"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Phone Number" required>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputCls}
                    placeholder="(555) 123-4567"
                    data-testid="ins-phone"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-email"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Preferred Contact Method" required>
                  <select
                    name="preferredContact"
                    required
                    value={formData.preferredContact}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-preferred"
                  >
                    <option value="">Select one</option>
                    <option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                    <option value="Text">Text</option>
                  </select>
                </Field>
                <Field label="State" required>
                  <select
                    name="state"
                    required
                    value={formData.state}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-state"
                  >
                    <option value="">Select state</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Date of Birth (Optional)">
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-dob"
                  />
                </Field>
                <Field label="Veteran Status" required>
                  <select
                    name="veteranStatus"
                    required
                    value={formData.veteranStatus}
                    onChange={handleChange}
                    className={inputCls}
                    data-testid="ins-veteran"
                  >
                    <option value="">Select one</option>
                    <option value="Veteran">Veteran</option>
                    <option value="Active Duty">Active Duty</option>
                    <option value="Military Spouse/Family">Military Spouse/Family</option>
                    <option value="Civilian">Civilian</option>
                  </select>
                </Field>
              </div>

              <Field label="Service Interested In" required>
                <select
                  name="serviceInterest"
                  required
                  value={formData.serviceInterest}
                  onChange={handleChange}
                  className={inputCls}
                  data-testid="ins-service"
                >
                  <option value="">Select a service</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Best Time to Contact">
                <input
                  type="text"
                  name="bestTimeToContact"
                  value={formData.bestTimeToContact}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="e.g., Weekdays after 5pm ET"
                  data-testid="ins-time"
                />
              </Field>

              <Field label="Additional Comments">
                <textarea
                  name="additionalComments"
                  rows={4}
                  value={formData.additionalComments}
                  onChange={handleChange}
                  className={`${inputCls} resize-none`}
                  placeholder="Anything you'd like us to know before we call"
                  data-testid="ins-comments"
                />
              </Field>

              <div className="bg-slate-100 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="w-4 h-4 mt-1"
                    data-testid="ins-consent"
                  />
                  <span className="text-sm text-slate-700">
                    <strong>Consent to Contact *</strong>
                    <br />I consent to be contacted by Noble Strategies Consulting regarding my
                    insurance inquiry.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-colors disabled:opacity-50 mt-4"
                data-testid="ins-submit"
              >
                {submitting ? "Sending..." : "Submit Inquiry"}
              </button>

              {status === "error" && (
                <p className="text-red-500 text-sm text-center">
                  Something went wrong. Please try again or call 786-645-7229 directly.
                </p>
              )}
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

export default InsuranceInquiryModal;
